import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import ShortUniqueId from 'short-unique-id';
import { AlertColor, Box } from '@mui/material';
import { useDrop } from 'react-dnd';
import {
  addCalculationField,
  setAggregationValue,
  setSource,
} from '../../../redux/Calculations/CalculationsActions';
import { isFlowUsed, isFlowAlreadyPresentInStep, isFlowFilledProperly } from '../utils';
interface IAggregationDropZone {
  children: React.ReactNode;
  propKey: string;
}
function AggregrationDropZone({ children, propKey }: IAggregationDropZone) {
  const uid = new ShortUniqueId({ length: 4 });
  const [showBorder, setShowBorder] = useState(false);
  const dispatch = useDispatch();
  const { calculationInfo, activeFlow, calculationUid } = useSelector((state: RootState) => {
    const currCalc = state.calculations.properties[propKey]?.currentCalculationSession;
    return {
      calculationInfo: currCalc?.calculationInfo || null,
      activeFlow: currCalc?.activeFlow || null,
      calculationUid: currCalc?.uuid || null,
    };
  });
  const disableUpdate = useMemo(() => {
    if (!calculationInfo || !activeFlow) return true;
    return isFlowUsed(
      Object.keys(calculationInfo.flows),
      calculationInfo.flows,
      activeFlow,
      calculationInfo,
    );
  }, [calculationInfo, activeFlow]);

  const [alert, setAlert] = useState<{
    severity: AlertColor;
    message: string;
  } | null>(null);

  const [{ isOver }, drop] = useDrop({
    accept: ['card', 'calculationSelected'],
    hover: (item: any, monitor) => {
      if (monitor.isOver({ shallow: true }) && !showBorder) {
        setShowBorder(true);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
    drop: (item: any, monitor) => {
      if (disableUpdate) {
        setAlert({
          severity: 'warning',
          message:
            'This calculation is being used in another flow. Please remove the dependency before deleting.',
        });
        return;
      }
      if (!activeFlow || !calculationInfo) return;
      if (item.type === 'calculationSelected') {
        const droppedItemAggregated = item.fieldData.isAggregated;
        if (droppedItemAggregated) {
          setAlert({
            severity: 'warning',
            message: 'Cannot drop a non-aggregated flow into an aggregated flow and vice versa',
          });
          return;
        }
        if (isFlowAlreadyPresentInStep(calculationInfo, item.uid, activeFlow, 0, 'source')) {
          setAlert({
            severity: 'warning',
            message: `This flow is already present inside this flow.`,
          });
          return;
        }
      }
      if (calculationUid) {
        const newDataType = item.fieldData.dataType === 'decimal' ? 'decimal' : 'integer';
        const prevResulttype = calculationInfo.flows[activeFlow][0].resultantDatatype;
        if (prevResulttype && prevResulttype !== newDataType) {
          setAlert({
            severity: 'error',
            message: `Cannot change the result type of already saved formula from ${prevResulttype} to ${newDataType}. Please create a new formula.`,
          });
          return;
        }
      }
      if (item.type === 'card') {
        const fieldId = uid();
        dispatch(
          addCalculationField(propKey, fieldId, {
            tableId: item.fieldData.tableId,
            fieldName: item.fieldData.fieldname,
            displayName: item.fieldData.displayname,
            dataType: item.fieldData.dataType,
          }),
        );
        dispatch(setSource(propKey, activeFlow, 0, fieldId, 'field', 0));
        dispatch(
          setAggregationValue(
            propKey,
            activeFlow,
            0,
            ['integer', 'decimal'].includes(item.fieldData.dataType) ? 'sum' : 'count',
            item.fieldData.dataType === 'decimal' ? 'decimal' : 'integer',
          ),
        );
      } else if (item.type === 'calculationSelected') {
        if (item.fieldData.isAggregated) {
          setAlert({
            severity: 'error',
            message: 'Cannot add an aggregated field to an aggregation drop zone.',
          });
          return;
        }
        if (!isFlowFilledProperly(calculationInfo, item.uid)) {
          setAlert({
            severity: 'warning',
            message: `Please ensure at least one field or flow is present and all conditions are met for flow ${item.name} before dropping.`,
          });
          return;
        }

        dispatch(setSource(propKey, activeFlow, 0, item.uid, 'flow', 0));
        dispatch(
          setAggregationValue(
            propKey,
            activeFlow,
            0,
            ['integer', 'decimal', 'date', 'timestamp'].includes(item.fieldData.dataType)
              ? 'sum'
              : 'count',
            item.fieldData.dataType === 'decimal' ? 'decimal' : 'integer',
          ),
        );
      }
      setShowBorder(false);
    },
  });
  useEffect(() => {
    if (!isOver) {
      setShowBorder(false);
    }
  }, [isOver]);
  return (
    <>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <Box
        ref={drop}
        className={`dropzone ${showBorder ? 'dropzone-highlighted' : ''}`}
        sx={{
          cursor: 'pointer',
          textAlign: 'left',
          paddingTop: '5px',
          borderBottom: !showBorder ? '1px solid  #999999' : 'none',
          height: 'auto',
          marginBottom: '0.5rem',
          border: '1px solid transparent',
        }}
      >
        {children}
      </Box>
    </>
  );
}

export default AggregrationDropZone;
